import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ReportBuilder = ({ isOpen, onClose, onSave }) => {
  const [reportName, setReportName] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [visualizationType, setVisualizationType] = useState('table');
  const [filters, setFilters] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const availableFields = [
    { value: 'product_name', label: 'Product Name', category: 'Product' },
    { value: 'sku', label: 'SKU', category: 'Product' },
    { value: 'barcode', label: 'Barcode Number', category: 'Product' },
    { value: 'category', label: 'Category', category: 'Product' },
    { value: 'current_stock', label: 'Current Stock', category: 'Inventory' },
    { value: 'reorder_point', label: 'Reorder Point', category: 'Inventory' },
    { value: 'unit_cost', label: 'Unit Cost', category: 'Financial' },
    { value: 'total_value', label: 'Total Value', category: 'Financial' },
    { value: 'supplier', label: 'Supplier', category: 'Supplier' },
    { value: 'location', label: 'Location', category: 'Location' }
  ];

  const visualizationOptions = [
    { value: 'table', label: 'Data Table' },
    { value: 'bar_chart', label: 'Bar Chart' },
    { value: 'line_chart', label: 'Line Chart' },
    { value: 'pie_chart', label: 'Pie Chart' },
    { value: 'summary_cards', label: 'Summary Cards' }
  ];

  const handleFieldToggle = (field) => {
    setSelectedFields(prev => 
      prev?.includes(field) 
        ? prev?.filter(f => f !== field)
        : [...prev, field]
    );
  };

  const handleSaveReport = async () => {
    setSaving(true);
    setSaveError('');
    const reportConfig = {
      name: reportName,
      fields: selectedFields,
      visualization: visualizationType,
      filters
    };
    try {
      await onSave(reportConfig);
    } catch (error) {
      console.error('Failed to create custom report:', error);
      setSaveError('The report could not be generated. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Custom Report Builder</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Report Configuration */}
            <div className="lg:col-span-1 space-y-6">
              <div>
                <Input
                  label="Report Name"
                  placeholder="Enter report name"
                  value={reportName}
                  onChange={(e) => setReportName(e?.target?.value)}
                  required
                />
              </div>

              <div>
                <Select
                  label="Visualization Type"
                  options={visualizationOptions}
                  value={visualizationType}
                  onChange={setVisualizationType}
                />
              </div>

              <div>
                <h3 className="text-sm font-medium text-foreground mb-3">Available Fields</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {availableFields?.map((field) => (
                    <div
                      key={field?.value}
                      className={`p-3 border rounded-lg cursor-pointer transition-hover ${
                        selectedFields?.includes(field?.value)
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                      onClick={() => handleFieldToggle(field?.value)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-foreground">{field?.label}</p>
                          <p className="text-xs text-muted-foreground">{field?.category}</p>
                        </div>
                        {selectedFields?.includes(field?.value) && (
                          <Icon name="Check" size={16} className="text-primary" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Report Preview */}
            <div className="lg:col-span-2">
              <div className="bg-muted rounded-lg p-6 h-full min-h-96">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Report Preview</h3>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="Eye" size={16} />
                    <span>Live Preview</span>
                  </div>
                </div>

                {selectedFields?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center">
                    <Icon name="FileText" size={48} className="text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Select fields to preview your report</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-card rounded-lg p-4">
                      <h4 className="font-medium text-foreground mb-2">{reportName || 'Untitled Report'}</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Fields: </span>
                          <span className="text-foreground">{selectedFields?.length}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Type: </span>
                          <span className="text-foreground capitalize">{visualizationType?.replace('_', ' ')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-card rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <Icon name="Table" size={16} className="text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">Selected Fields</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedFields?.map((field) => {
                          const fieldInfo = availableFields?.find(f => f?.value === field);
                          return (
                            <div key={field} className="flex items-center space-x-2 text-sm">
                              <Icon name="Check" size={12} className="text-success" />
                              <span className="text-foreground">{fieldInfo?.label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          {saveError && <p role="alert" className="mr-auto text-sm text-destructive">{saveError}</p>}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            variant="default" 
            onClick={handleSaveReport}
            disabled={!reportName.trim() || selectedFields?.length === 0 || saving}
            iconName="Save"
            iconPosition="left"
            iconSize={16}
          >
            {saving ? 'Generating...' : 'Generate Report'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;